<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { HTMLButtonAttributes, MouseEventHandler } from 'svelte/elements';

  type Variant = 'primary' | 'secondary' | 'tertiary' | 'quiet' | 'danger';

  interface Props extends Omit<HTMLButtonAttributes, 'class' | 'children'> {
    variant?: Variant;
    block?: boolean;
    compact?: boolean;
    underline?: boolean;
    pending?: boolean;
    pendingLabel?: string;
    href?: string;
    target?: string;
    rel?: string;
    class?: string;
    iconStart?: Snippet;
    iconEnd?: Snippet;
    children: Snippet;
  }

  let {
    variant = 'secondary',
    block = false,
    compact = false,
    underline = false,
    pending = false,
    pendingLabel,
    href,
    target,
    rel,
    class: className = '',
    iconStart,
    iconEnd,
    children,
    onclick,
    type = 'button',
    ...rest
  }: Props = $props();

  const BUTTON_ONLY = new Set([
    'disabled',
    'form',
    'formaction',
    'formenctype',
    'formmethod',
    'formnovalidate',
    'formtarget',
    'name',
    'value',
    'popovertarget',
    'popovertargetaction',
  ]);
  const linkAttributes = $derived(Object.fromEntries(Object.entries(rest).filter(([key]) => !BUTTON_ONLY.has(key))));
  // A link has no pending state, so its click handler passes straight through.
  const linkClick = $derived(onclick as unknown as MouseEventHandler<HTMLAnchorElement> | null | undefined);

  const classes = $derived(
    [
      'stat-button',
      `stat-button--${variant}`,
      block ? 'stat-button--block' : '',
      compact ? 'stat-button--compact' : '',
      underline ? 'stat-button--underline' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')
  );
</script>

{#if href}
  <a {...linkAttributes} {href} {target} {rel} class={classes} onclick={linkClick}>
    {@render iconStart?.()}
    <span>{@render children()}</span>
    {@render iconEnd?.()}
  </a>
{:else}
  <button
    {...rest}
    {type}
    class={classes}
    aria-disabled={pending ? 'true' : rest['aria-disabled']}
    aria-busy={pending ? 'true' : undefined}
    onclick={(event) => {
      if (pending) {
        event.preventDefault();
        return;
      }
      onclick?.(event);
    }}
  >
    {#if pending}
      <span class="stat-spinner stat-spinner--small" aria-hidden="true"></span>
    {:else}
      {@render iconStart?.()}
    {/if}
    <span>
      {#if pending && pendingLabel}{pendingLabel}{:else}{@render children()}{/if}
    </span>
    {#if !pending}{@render iconEnd?.()}{/if}
  </button>
{/if}
